// Phase 14e-c-4 — /admin/payouts overview page.
//
// Wednesday DRAFT-review workflow per `manage-payouts.md` §0 first
// runbook query: "What DRAFT payouts are waiting for me to review and
// issue?" Cross-caregiver global view (PayoutsCard on caregiver detail
// is single-caregiver-scoped). Per 14e-c-4 lock: filter chips for
// status + payoutFrequency; click row → navigate to caregiver detail
// (PayoutsCard hosts the full per-row action surface).
//
// Force-cycle-close lives on PayoutsCard per 14e-c-6, NOT on this
// page (it's a per-cycle post-VOID recovery action, not a global
// overview action).

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader } from '@truecare/ui';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';
import { AdminFilterChips } from '../components/AdminFilterChips';
import { adminBeforeLoad } from '../lib/admin-before-load';

export const Route = createFileRoute('/payouts')({
  // The dashboard "Failed payouts" card drills in with `?needsAttention=true` so
  // the page opens pre-filtered to exactly the payouts it counted.
  validateSearch: (
    search: Record<string, unknown>,
  ): { needsAttention?: boolean } => ({
    needsAttention:
      search['needsAttention'] === true || search['needsAttention'] === 'true',
  }),
  beforeLoad: () => adminBeforeLoad('/payouts'),
  component: PayoutsOverviewPage,
});

interface PayoutRow {
  id: string;
  payoutNumber: string | null;
  caregiverId: string;
  caregiverName: string;
  caregiverEmail: string | null;
  status: string;
  totalCents: number;
  cycleStartDate: string;
  cycleEndDate: string;
  issuedAt: string | null;
  paidAt: string | null;
  payoutFrequency: 'WEEKLY' | 'BIWEEKLY';
}

// 'needs_attention' is the dashboard "Failed payouts" view — stale DRAFTs +
// ISSUED-with-rejected-EFT. It's not a payout status; the backend resolves it
// via the shared needsAttention selector, so the count matches the card.
type StatusFilter =
  | 'needs_attention'
  | 'all'
  | 'DRAFT'
  | 'ISSUED'
  | 'PAID'
  | 'VOIDED';
type FrequencyFilter = 'all' | 'WEEKLY' | 'BIWEEKLY';

function PayoutsOverviewPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  // Default to DRAFT for the Wednesday review workflow (`manage-payouts.md` §0),
  // unless the dashboard "Failed payouts" card sent us here pre-filtered.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    search.needsAttention ? 'needs_attention' : 'DRAFT',
  );
  const [frequencyFilter, setFrequencyFilter] = useState<FrequencyFilter>('all');

  // Cadence filtering is server-side (the `payoutFrequency` query param) — so we
  // page normally. The previous client-side approach fetched an oversized page
  // (pageSize=500) to filter locally, but 500 exceeds the endpoint's max of 100,
  // so selecting a cadence 400'd the request and the list showed "Couldn't load."
  const listQuery = useQuery({
    queryKey: ['admin', 'payouts', { statusFilter, frequencyFilter, page, limit }],
    queryFn: async () => {
      const queryParam: Record<string, string> = {
        page: String(page),
        pageSize: String(limit),
      };
      // 'needs_attention' is server-side via its own param (spans DRAFT + ISSUED,
      // so it isn't a `status` value); otherwise pass the status filter.
      if (statusFilter === 'needs_attention') {
        queryParam['needsAttention'] = 'true';
      } else if (statusFilter !== 'all') {
        queryParam['status'] = statusFilter;
      }
      if (frequencyFilter !== 'all') queryParam['payoutFrequency'] = frequencyFilter;

      const r = (await unwrap(
        api.GET('/api/v1/admin/payouts' as never, {
          params: { query: queryParam },
        } as never),
      )) as { items: PayoutRow[]; totalCount: number };
      return r;
    },
  });

  const visibleItems = listQuery.data?.items ?? [];
  const effectiveTotalCount = listQuery.data?.totalCount ?? 0;

  // Column order (Phase 17 M2): Status sits second, right after Number, so the
  // review-decision column stays on-screen on a phone before the wide
  // Professional / Cycle columns scroll it off the right edge.
  const columns: AdminTableColumn<PayoutRow>[] = [
    {
      key: 'payoutNumber',
      header: 'Number',
      width: 'w-40',
      render: (r) => r.payoutNumber ?? '—',
    },
    { key: 'status', header: 'Status', width: 'w-28' },
    { key: 'caregiverName', header: 'Professional', width: 'w-48' },
    {
      key: 'cycleStartDate',
      header: 'Cycle',
      width: 'w-44',
      render: (r) => `${r.cycleStartDate} → ${r.cycleEndDate}`,
    },
    {
      key: 'totalCents',
      header: 'Total',
      width: 'w-28',
      render: (r) => formatCents(r.totalCents),
    },
    { key: 'payoutFrequency', header: 'Cadence', width: 'w-28' },
    {
      key: 'issuedAt',
      header: 'Issued',
      width: 'w-32',
      render: (r) => (r.issuedAt ? r.issuedAt.slice(0, 10) : '—'),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">Payouts</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Every professional's payouts in one place. Opens showing draft
            payouts waiting for the Wednesday review. Click a row to open that
            professional's page, where you can issue a payout, record a payment,
            or void one.
          </p>
        </header>

        <Card padding="md">
          <CardHeader title="Filters" />
          <div className="mt-3 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted mb-1">
                Status
              </p>
              <AdminFilterChips
                value={statusFilter}
                options={
                  [
                    ['needs_attention', 'Needs attention'],
                    ['DRAFT', 'DRAFT (default)'],
                    ['ISSUED', 'ISSUED'],
                    ['PAID', 'PAID'],
                    ['VOIDED', 'VOIDED'],
                    ['all', 'All'],
                  ] as const
                }
                onChange={(v) => {
                  setStatusFilter(v);
                  setPage(1);
                }}
                ariaLabel="Filter payouts by status"
                testIdPrefix="payouts-status"
              />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted mb-1">
                Cadence
              </p>
              <AdminFilterChips
                value={frequencyFilter}
                options={
                  [
                    ['all', 'All'],
                    ['BIWEEKLY', 'Biweekly'],
                    ['WEEKLY', 'Weekly'],
                  ] as const
                }
                onChange={(v) => {
                  setFrequencyFilter(v);
                  setPage(1);
                }}
                ariaLabel="Filter payouts by cadence"
                testIdPrefix="payouts-frequency"
              />
            </div>
          </div>
        </Card>

        <Card padding="md">
          <CardHeader title={`${effectiveTotalCount} payout${effectiveTotalCount === 1 ? '' : 's'}`} />
          <div className="mt-3">
            <AdminTable
              columns={columns}
              rows={visibleItems}
              isLoading={listQuery.isPending}
              isError={listQuery.isError}
              emptyMessage="No payouts match the current filter."
              page={page}
              limit={limit}
              totalCount={effectiveTotalCount}
              onPageChange={setPage}
              onLimitChange={(n) => {
            // 2026-07-23 audit (U4): resetting to page 1 is required — the
            // backends compute skip=(page-1)*pageSize with no clamping, so
            // growing the page size while on page 2+ can put skip past
            // totalCount and render "no results" over a populated list.
            setLimit(n);
            setPage(1);
          }}
              onRowClick={(r) =>
                navigate({ to: '/caregivers/$id', params: { id: r.caregiverId } })
              }
            />
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${(abs / 100).toFixed(2)}`;
}
